import { Link, Stack, Typography } from "@mui/material";

import { isSmallDesktop } from "@hooks/layout";

import styled, { css, keyframes } from "styled-components";

export const AlbumTitle = styled(Typography)`
  color: #fff;
  font-family: Kaisei Opti;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 135%;

  text-decoration-thickness: 1px;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;

  &:hover {
    text-decoration-line: underline;
  }

  cursor: pointer;

  ${isSmallDesktop} {
    color: #fff;
    font-size: 1.5rem;
  }
`;

export const AlbumAuthorName = styled.span`
  color: #e3bda0;
  font-family: Kaisei Opti;
  font-size: 0.625rem;
  font-weight: 400;
  line-height: 100%;
  text-transform: uppercase;

  text-decoration-thickness: 1px;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;

  &:hover {
    text-decoration-line: underline;
  }

  cursor: pointer;

  ${isSmallDesktop} {
    font-size: 0.75rem;
  }
`;

export const AlbumAuthorDetails = styled(Link)`
  color: ${({ theme }) => theme.palette.textColor.highlighted};
  font-family: Raleway;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 300;
  line-height: 120%;
  cursor: pointer;

  ${isSmallDesktop} {
    font-size: 1rem;
  }
`;

export const AlbumDescription = styled(Typography)<{ $isCollapsed?: boolean }>`
  color: ${({ theme }) => theme.palette.textColor.common};
  font-family: Raleway;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $isCollapsed }) => ($isCollapsed ? 10 : 12)};
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AvaibleForTypography = styled(Typography)`
  color: ${({ theme }) => theme.palette.textColor.header};
  font-family: Raleway;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 1.225rem */
`;

const albumCoverFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const AlbumCover = styled.img<{
  isLoading?: boolean;
}>`
  border-radius: 8px;
  border: none;

  aspect-ratio: 1 / 1;
  object-fit: contain;

  width: 100%;

  height: auto;

  box-shadow: none;
  transition: box-shadow 0.3s ease-in-out;
  /* 700ms fade-in on every mount — runs regardless of whether the image
     is cold-cache or warm-cache, so the visual transition is always visible.
     Trade-off: cached images take ~700ms to become fully visible (delays
     are tolerable for cover thumbnails where the fade reads as polish). */
  animation: ${albumCoverFadeIn} 700ms ease both;

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.3) 0px 7px 29px 0px;
  }

  cursor: pointer;

  /* Skeleton overlay (sibling in AlbumList) covers the image while it's
     still in-flight; the fade-in animation handles the cosmetic reveal. */
  ${({ isLoading }) => isLoading && "visibility: hidden;"}
`;

export const FiltersContainer = styled(Stack)`
  width: 100%;
`;

export const FilterName = styled(Typography)`
  color: ${({ theme }) => theme.palette.textColor.common};
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 100%; /* 0.875rem */
`;

export const SongStack = styled.div<{ $isSemiTransparent?: boolean; $isActive: boolean; $flexInfo?: boolean; $variant?: "phone" | "tablet" | "desktop" }>`
  display: grid;
  grid-template-columns: ${({ $variant, $flexInfo }) =>
    $variant === "phone"
      ? "48px minmax(0, 1fr) auto"
      : $variant === "tablet"
        ? "40px 48px minmax(140px, 1fr) 60px auto auto"
        : `40px 48px ${$flexInfo ? "minmax(100px, 160px)" : "minmax(140px, 220px)"} 1fr 60px auto auto`};
  align-items: center;
  gap: ${({ $variant }) => ($variant === "phone" ? "12px" : $variant === "tablet" ? "10px" : "14px")};
  padding: ${({ $variant }) => ($variant === "phone" ? "9px 12px" : $variant === "tablet" ? "10px 12px" : "12px 18px")};
  background: transparent;
  border: 1px solid transparent;
  border-radius: 10px;
  transition: all 0.3s ease;
  position: relative;

  /* Hover state (non-playing).
     All hover rules are gated behind @media (hover: hover) so they apply ONLY
     to devices with a real pointer. On touchscreens iOS leaves :hover STUCK on
     the last-tapped element until you tap elsewhere — without this guard, the
     stuck hover forced the heart svg to fill:#fff !important after a tap, which
     masked the real favourite-state change until a scroll released the hover
     (the "heart only flips on scroll" bug on /music). */
  ${({ $isActive }) =>
    !$isActive &&
    css`
      @media (hover: hover) {
        &:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: rgba(255, 255, 255, 0.04);
        }

        &:hover .play-btn {
          border-color: rgba(255, 255, 255, 0.35);
          color: rgba(255, 255, 255, 0.8);
        }

        &:hover .track-action-btn:hover::before {
          opacity: 1 !important;
        }
        &:hover .track-action-btn:hover {
          color: #0a1a2a !important;
          background: transparent !important;
          box-shadow: 0 0 12px rgba(156, 211, 253, 0.6);
        }
        &:hover .track-action-btn:hover svg {
          stroke: #0a1a2a !important;
        }

        &:hover .track-btn {
          background: rgba(255, 255, 255, 0.1) !important;
          color: #e0d2c1 !important;
        }
        &:hover .track-btn:hover {
          background: rgba(255, 255, 255, 0.18) !important;
          color: #e0d2c1 !important;
        }

        &:hover .heart-btn:hover {
          color: #ffffff !important;
        }
        &:hover .heart-btn:hover svg {
          fill: #ffffff !important;
          stroke: #ffffff !important;
        }
      }
    `}

  /* Playing/active state — base styling always applies; the :hover sub-rules are
     gated behind @media (hover: hover) for the same touch-sticky-hover reason. */
  ${({ $isActive }) =>
    $isActive &&
    css`
      background: rgba(186, 144, 92, 0.3);
      border-color: rgba(255, 220, 168, 0.5);
      box-shadow: 0 0 18px rgba(186, 144, 92, 0.15), 0 0 6px rgba(255, 220, 168, 0.1);

      .play-btn {
        background: #ffdca8 !important;
        border-color: #ffdca8 !important;
        color: #5a3d2a !important;
      }

      .track-action-btn::before {
        opacity: 0 !important;
      }

      .track-btn {
        background: rgba(255, 255, 255, 0.1) !important;
        color: #e0d2c1 !important;
      }

      @media (hover: hover) {
        .play-btn:hover {
          box-shadow: 0 0 12px rgba(255, 220, 168, 0.4);
        }

        .track-action-btn:hover::before {
          opacity: 1 !important;
        }
        .track-action-btn:hover {
          color: #0a1a2a !important;
          background: transparent !important;
          box-shadow: 0 0 12px rgba(156, 211, 253, 0.6);
        }
        .track-action-btn:hover svg {
          stroke: #0a1a2a !important;
        }

        .heart-btn:hover {
          color: #ffffff !important;
          background: rgba(255, 255, 255, 0.18) !important;
        }
        .heart-btn:hover svg {
          fill: #ffffff !important;
          stroke: #ffffff !important;
        }
      }
    `}
`;

export const SongTitle = styled(Typography)`
  color: #ffffff;
  font-family: "Cormorant Garamond", serif;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 500;
  line-height: 1.1;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  text-decoration-thickness: 1px;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;

  &:hover {
    text-decoration-line: underline;
  }

  cursor: pointer;

  @media only screen and (max-width: 365px) {
    font-size: 1rem;
    line-height: 130%;
  }
`;

export const SongAuthor = styled(Typography)`
  color: #efd0b8;
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.2;

  .composer {
    text-decoration-thickness: 1px;
    text-decoration-skip-ink: auto;
    text-underline-offset: 2px;

    &:hover {
      text-decoration-line: underline;
    }

    cursor: pointer;
  }
`;

export const FiltersTypography = styled(Typography)`
  color: ${({ theme }) => theme.palette.textColor.header};
  font-family: Kaisei Opti;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 500;
  line-height: 100%; /* 1.125rem */
  letter-spacing: 0.01125rem;
  text-transform: capitalize;
`;

export const SearchElement = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;

  background-color: transparent;
  padding: 4px 8px 4px;
  border-radius: 4px;

  &:hover {
    cursor: pointer;
    background-color: #5c412a;
  }
`;
